import { ESessionPhase, ESocketEvents } from '@lbs/shared-rp-types'
import { MachineOptions, assign } from 'xstate'
import i18n from 'core/i18n'
import stores from 'stores'
import {
  ERpAction,
  ERpEvent,
  JOIN_GAME_TIMEOUT_ERROR,
  RussianPokerMachineContext,
  RussianPokerMachineEvent,
  RussianPokerMachineRuntime,
} from './types'

type RpGuards = MachineOptions<
  RussianPokerMachineContext,
  RussianPokerMachineEvent
>['guards']

const getJoinErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'Failed to join game'
}

type RpActions = MachineOptions<
  RussianPokerMachineContext,
  RussianPokerMachineEvent
>['actions']

export const createRpActions = (
  getRuntime: () => RussianPokerMachineRuntime
): RpActions => ({
  [ERpAction.PatchStoreFromJoinResult]: (
    _ctx: RussianPokerMachineContext,
    event: RussianPokerMachineEvent
  ) => {
    if (event.type !== ERpEvent.JoinSuccess) return

    const { store } = getRuntime()
    const { game, session, deposit, boxes } = event.data

    store.patchStore({ game, session, deposit })
    store.setBoxes(boxes)
  },

  [ERpAction.NavigateLobby]: () => {
    getRuntime().navigateLobby()
  },

  [ERpAction.EmitLeaveGame]: (ctx: RussianPokerMachineContext) => {
    getRuntime().socket.emit(ESocketEvents.LEAVE_GAME, {
      gameId: ctx.gameId,
    })
  },

  [ERpAction.EmitForceLeaveGame]: (ctx: RussianPokerMachineContext) => {
    getRuntime().socket.emit(ESocketEvents.LEAVE_GAME, {
      gameId: ctx.gameId,
      force: true,
    })
  },

  [ERpAction.ClearError]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    errorMessage: () => null,
  }),

  [ERpAction.SetJoinError]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    errorMessage: (_ctx, event) => {
      if (event.type !== ERpEvent.JoinFail) return null
      return getJoinErrorMessage(event.error)
    },
  }),

  [ERpAction.ShowJoinFailToast]: (
    _ctx: RussianPokerMachineContext,
    event: RussianPokerMachineEvent
  ) => {
    if (event.type !== ERpEvent.JoinFail) return
    if (!(event.error instanceof Error)) return
    if (event.error.message !== JOIN_GAME_TIMEOUT_ERROR) return

    stores.notificationStore.showError(i18n.t('common.joinGameTimeout'))
  },

  [ERpAction.SetSessionId]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    sessionId: (_ctx, event) => {
      if (event.type === ERpEvent.JoinSuccess) return event.data.game.sessionId
      if (event.type === ERpEvent.NewSession) return event.sessionId
      return _ctx.sessionId
    },
  }),

  [ERpAction.SetBoxOnTurn]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    boxOnTurn: (ctx, event) => {
      if (event.type !== ERpEvent.BoxTurnReceived) return ctx.boxOnTurn
      return event.seatNumber
    },
  }),

  // Reads boxOnTurn from store snapshot — used in syncing state on join/reconnect.
  [ERpAction.SetBoxOnTurnFromSnapshot]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    boxOnTurn: () => getRuntime().store.session?.boxOnTurn ?? null,
  }),

  [ERpAction.EnqueueCard]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    cardQueue: (ctx, event) => {
      if (event.type !== ERpEvent.CardQueued) return ctx.cardQueue
      return [...ctx.cardQueue, event.item]
    },
  }),

  // Wraps BoxPatchReceived into a CardQueueItem for the dealing animation queue.
  [ERpAction.EnqueueBoxPatch]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    cardQueue: (ctx, event) => {
      if (event.type !== ERpEvent.BoxPatchReceived) return ctx.cardQueue
      return [
        ...ctx.cardQueue,
        { target: 'box' as const, data: event.boxPatch },
      ]
    },
  }),

  [ERpAction.ApplyNextCard]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    cardQueue: (ctx) => {
      const item = ctx.cardQueue[0]
      if (!item) return ctx.cardQueue

      const { store } = getRuntime()
      if (item.target === 'box') {
        store.patchBox(item.data)
      } else {
        store.patchSession(item.data)
      }

      return ctx.cardQueue.slice(1)
    },
  }),

  [ERpAction.DeferDecisionsPhase]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    deferredPhaseAfterDealing: (_ctx, event) => {
      if (event.type !== ERpEvent.SessionPhaseReceived) return null
      return event.phase
    },
  }),

  [ERpAction.ClearDeferredPhaseAfterDealing]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    deferredPhaseAfterDealing: () => null,
  }),

  [ERpAction.ClearCardQueue]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    cardQueue: () => [],
  }),

  // Apply raw session socket payload to MobX store.
  // Called as a machine action so the patch happens atomically with the state transition,
  // eliminating the MobX/XState desync that occurs when patching in service callbacks.
  [ERpAction.ApplySessionPatch]: (
    _ctx: RussianPokerMachineContext,
    event: RussianPokerMachineEvent
  ) => {
    if ('sessionPatch' in event && event.sessionPatch) {
      getRuntime().store.patchSession(event.sessionPatch)
    }
  },

  // Apply raw box socket payload to MobX store — same rationale as ApplySessionPatch.
  [ERpAction.ApplyBoxPatch]: (
    _ctx: RussianPokerMachineContext,
    event: RussianPokerMachineEvent
  ) => {
    if (event.type === ERpEvent.BoxPatchReceived) {
      getRuntime().store.patchBox(event.boxPatch)
    }
  },

  [ERpAction.StoreDeferredSessionPatch]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    deferredSessionPatches: (ctx, event) => {
      const patch =
        'sessionPatch' in event && event.sessionPatch
          ? event.sessionPatch
          : null
      return patch
        ? [...ctx.deferredSessionPatches, patch]
        : ctx.deferredSessionPatches
    },
  }),

  [ERpAction.ApplyDeferredSessionPatches]: (
    ctx: RussianPokerMachineContext
  ) => {
    const { store } = getRuntime()
    ctx.deferredSessionPatches.forEach((patch) => store.patchSession(patch))
  },

  [ERpAction.ClearDeferredSessionPatches]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    deferredSessionPatches: () => [],
  }),

  [ERpAction.StoreDeferredBoxOnTurn]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    deferredBoxOnTurn: (ctx, event) => {
      if (event.type !== ERpEvent.BoxTurnReceived) return ctx.deferredBoxOnTurn
      return event.seatNumber
    },
  }),

  // Flush buffered boxOnTurn from dealing phase.
  // The decisions.waitingActiveBox always guard will check deferredBoxPatchesBySeat.
  [ERpAction.FlushDeferredBoxOnTurn]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    boxOnTurn: (ctx) => ctx.deferredBoxOnTurn ?? ctx.boxOnTurn,
    deferredBoxOnTurn: () => null,
  }),

  // Store latest box patch per seat (copy-on-write).
  // Overwrites previous for same seat — last BOX event has real cards.
  [ERpAction.StoreDeferredBoxPatch]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    deferredBoxPatchesBySeat: (ctx, event) => {
      if (event.type !== ERpEvent.BoxPatchReceived)
        return ctx.deferredBoxPatchesBySeat
      return {
        ...ctx.deferredBoxPatchesBySeat,
        [event.seatNumber]: event.boxPatch,
      }
    },
  }),

  // Apply the deferred box patch for the current boxOnTurn seat and clear it from context.
  [ERpAction.ApplyAndClearDeferredBoxPatch]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    deferredBoxPatchesBySeat: (ctx) => {
      const seat = ctx.boxOnTurn
      if (seat !== null && ctx.deferredBoxPatchesBySeat[seat]) {
        getRuntime().store.patchBox(ctx.deferredBoxPatchesBySeat[seat])
      }
      if (seat === null) return ctx.deferredBoxPatchesBySeat
      const updated = { ...ctx.deferredBoxPatchesBySeat }
      delete updated[seat]
      return updated
    },
  }),

  // Apply raw game socket payload to MobX store (GAME events, NewSession).
  [ERpAction.ApplyGamePatch]: (
    _ctx: RussianPokerMachineContext,
    event: RussianPokerMachineEvent
  ) => {
    if ('gamePatch' in event && event.gamePatch) {
      getRuntime().store.patchGame(event.gamePatch)
    }
  },

  // Apply deposit update to MobX store.
  [ERpAction.ApplyDeposit]: (
    _ctx: RussianPokerMachineContext,
    event: RussianPokerMachineEvent
  ) => {
    if (event.type === ERpEvent.DepositReceived) {
      getRuntime().store.setDeposit(event.depositData)
    }
  },

  [ERpAction.ResetTransientContext]: assign<
    RussianPokerMachineContext,
    RussianPokerMachineEvent
  >({
    boxOnTurn: () => null,
    cardQueue: () => [],
    deferredPhaseAfterDealing: () => null,
    deferredSessionPatches: () => [],
    deferredBoxOnTurn: () => null,
    deferredBoxPatchesBySeat: () => ({}),
  }),
})

export const createRpGuards = (
  getRuntime: () => RussianPokerMachineRuntime
): RpGuards => ({
  hasCardInQueue: (ctx) => ctx.cardQueue.length > 0,
  hasDeferredDecisionsPhase: (ctx) => ctx.deferredPhaseAfterDealing !== null,

  // Guards for syncing state — read store snapshot on join/reconnect.
  isSnapshotDealing: () =>
    getRuntime().store.session?.phase === ESessionPhase.DEALING,
  isSnapshotDecisions: () =>
    getRuntime().store.session?.phase === ESessionPhase.DECISIONS,
  isSnapshotDecisionsWithReadyBox: () => {
    const { store } = getRuntime()
    if (store.session?.phase !== ESessionPhase.DECISIONS) return false
    const boxOnTurn = store.session?.boxOnTurn
    const activeBox = store.boxes.find((b) => b.seatNumber === boxOnTurn)
    return activeBox?.cards?.some((c) => c !== 'XX') === true
  },
  isSnapshotSettlement: () =>
    getRuntime().store.session?.phase === ESessionPhase.SETTLEMENT,
  isSnapshotFinished: () =>
    getRuntime().store.session?.phase === ESessionPhase.FINISHED,

  // Guards for decisions substates.
  isCurrentSeatPatch: (ctx, event) =>
    event.type === ERpEvent.BoxPatchReceived && ctx.boxOnTurn === event.seatNumber,

  isPatchForCurrentBoxOnTurn: (ctx, event) => {
    if (event.type !== ERpEvent.BoxPatchReceived) return false
    if (ctx.boxOnTurn !== event.seatNumber) return false
    const cards = event.boxPatch?.data?.cards
    return Array.isArray(cards) && cards.some((card) => card !== 'XX')
  },
  hasDeferredPatchForCurrentBoxOnTurn: (ctx) =>
    ctx.boxOnTurn !== null &&
    (() => {
      const patch = ctx.deferredBoxPatchesBySeat[ctx.boxOnTurn]
      const cards = patch?.data?.cards
      return Array.isArray(cards) && cards.some((card) => card !== 'XX')
    })(),
})
